import { computed, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router/composables';

import { useConfig } from '@/stores/useConfig';
import { useChatWidget, InteractionView } from '@/stores/useChatWidget';
import { useMockedFlowStore } from './useMockedFlowStore';
import { mockUserLogin } from './mocks';
import { MockedFlowConfig, MockedFlowActions } from './types';
import { REGISTRY_FLOW_MAP } from './registryFlowMap';
import { devLog } from '@/common/debug';

/**
 * Composable to handle mocked chatbot flow based on initial registry ID.
 * Allows parent applications to start the chatbot from a specific page/screen.
 */
export const useMockedFlow = () => {
  const configStore = useConfig();
  const { config } = storeToRefs(configStore);

  const router = useRouter();
  const { setIsChatOverlayVisible, setInteractionView } = useChatWidget();
  const mockedFlowStore = useMockedFlowStore();

  const mockedFlowConfig = computed<MockedFlowConfig>(() => ({
    enabled: Boolean(config.value?.initialRegistryId),
    registryId: config.value?.initialRegistryId,
  }));

  const isMockedFlowEnabled = computed(() => mockedFlowConfig.value.enabled);

  const shouldSkipDefaultFlow = computed(() => {
    if (!mockedFlowConfig.value.enabled || !mockedFlowConfig.value.registryId) {
      return false;
    }

    const actions = REGISTRY_FLOW_MAP[mockedFlowConfig.value.registryId];
    return actions?.skipDefaultFlow ?? false;
  });

  /**
   * Get the flow actions for the current registry ID
   */
  const getFlowActions = (): MockedFlowActions | null => {
    const registryId = mockedFlowConfig.value.registryId;
    if (!registryId) return null;

    return REGISTRY_FLOW_MAP[registryId] || null;
  };

  /**
   * Execute the mocked flow based on the initial registry ID.
   * Returns true if the mocked flow was executed, false otherwise.
   */
  const executeMockedFlow = (): boolean => {
    devLog.debug('[MOCKED_FLOW] Mocked flow start', config.value);

    if (!mockedFlowConfig.value.enabled || !mockedFlowConfig.value.registryId) {
      devLog.debug('[MOCKED_FLOW] Mocked flow not enabled or registry ID not set');
      return false;
    }

    const registryId = mockedFlowConfig.value.registryId;
    const actions = getFlowActions();

    if (!actions) {
      devLog.warn(`[MOCKED_FLOW] No actions found for registry ID ${registryId}`);
      return false;
    }

    devLog.debug(`[MOCKED_FLOW] Executing mocked flow for ${registryId}`, actions);

    // Reset store so previous registry state (e.g. MODAL resumableChat) is not carried over
    mockedFlowStore.reset();

    // Setup user (guest vs authenticated, defaults to mockUserLogin)
    const setupUserFn = actions.setupUser ?? mockUserLogin;
    setupUserFn();

    // Get mock data and update store in one call
    const mockData = actions.getMockData?.(registryId);

    devLog.debug('[MOCKED_FLOW] Setting mocked data', mockData);
    mockedFlowStore.setMockedFlow({
      registryId,
      ...mockData,
      navigateTo: actions.navigateTo ?? null,
      skipDefaultFlow: actions.skipDefaultFlow ?? true,
      showChatOverlay: actions.showChatOverlay ?? true,
      interactionView: actions.interactionView ?? null,
    });

    devLog.debug('[MOCKED_FLOW] Mocked flow store state:', { ...mockedFlowStore.state });

    // Always set interaction view so previous view (e.g. CSAT) is cleared when switching registry
    setInteractionView(actions.interactionView ?? InteractionView.INPUT);

    // Navigate first so we're on the correct page (e.g. /new-home) before showing overlay.
    // When already on same route (e.g. HOME → CHATBOT), overlay must be set after navigation
    // so it is not lost in the same-tick update cycle.
    if (actions.navigateTo) {
      router.push(actions.navigateTo);
    }

    // Show or hide chat overlay after navigation / current tick so it sticks when switching from home
    if (actions.showChatOverlay) {
      devLog.debug('[MOCKED_FLOW] Showing chat overlay');
      nextTick(() => setIsChatOverlayVisible(true));
    } else {
      devLog.debug('[MOCKED_FLOW] Hiding chat overlay');
      setIsChatOverlayVisible(false);
    }

    return true;
  };

  /**
   * Clear the mocked flow configuration after execution.
   * Call this after the mocked flow has been processed to prevent re-execution.
   */
  const clearMockedFlow = () => {
    if (config.value?.initialRegistryId) {
      configStore.setConfig({ initialRegistryId: undefined });
    }
  };

  /**
   * Hide the chat overlay and reset mocked flow state.
   * Used when CUSTOMISATION_UPDATE sends initialRegistryId undefined (e.g. back to home).
   */
  const hideOverlayAndResetMockedFlow = () => {
    setInteractionView(InteractionView.INPUT);
    setIsChatOverlayVisible(false);
    mockedFlowStore.reset();
    configStore.setConfig({ initialRegistryId: undefined });
  };

  return {
    mockedFlowConfig,
    isMockedFlowEnabled,
    shouldSkipDefaultFlow,
    getFlowActions,
    executeMockedFlow,
    clearMockedFlow,
    hideOverlayAndResetMockedFlow,
  };
};
